/**
 * Ресурс «Аномалии v3» (L2) — case-level витрина новой anomaly-модели
 * (`anomaly_v1`, сценарии 1–5). Отдельный ресурс рядом с legacy `anomalies.api.ts`:
 * зерно другое (кейс + сценарий вместо пары), контракт другой, шкала уровней
 * четырёхступенчатая. Legacy-ресурс остаётся нетронутым как путь отката.
 *
 * МАРШРУТ тот же, что у legacy-ресурса: выделенного gateway-маршрута «analytics»
 * пока нет, поэтому витрины отдаёт transit-ai —
 * `/api/ai/v1/analytics/anomalies` → gateway (маршрут ai) → под transit-ai →
 * сервис аномалий на claude-host. Отличие только в параметре `source=new`:
 * им transit-ai выбирает новый case-level слой вместо legacy v2.
 *
 * ВАЖНО: anomaly score и ML-балл (Track A) — две независимые величины. Их
 * НЕЛЬЗЯ складывать, усреднять или сводить в один «общий риск».
 */

import { http, ApiError } from '@/lib/api/http'
import { MAX_EVIDENCE_EXAMPLES } from './evidence-row'
import type { EvidenceExamplesData } from './cross-unit/cross-unit.evidence-examples'
import type { HistoryDetailResponse } from './history-detail.types'
import type {
  AnomalyCasesListResponse,
  AnomalyCasesSummary,
  AnomalyCaseDetailResponse,
  SimilarCasesResponse,
} from './anomaly-cases-v3.types'

const BASE = '/api/ai/v1/analytics/anomalies'

// 401 от upstream-сервиса аномалий НЕ должен разлогинивать пользователя из
// платформы (сессии независимы) — поэтому skipAuthRedirect на всех запросах.
const OPTS = { skipAuthRedirect: true, cache: 'no-store' as RequestCache }

/** Фильтры case-level витрины. Пустые значения не отправляются (см. http.buildQuery). */
export interface CasesV3Params {
  scenario_id?: number
  anomaly_level?: string
  ml_level?: string
  anomaly_cohort?: string
  search?: string
  // exclude_eaeu / hide_empty_route are gone: the service now applies a single
  // route-eligibility rule to list, summary, search and filters alike, so there
  // is nothing left for the UI to toggle. The API still accepts the old params
  // for other callers.
  page?: number
  page_size?: number
  sort_by?: string
  sort_dir?: string
  /** Период по дате раскредитации импорта, YYYY-MM-DD, границы включительно. */
  date_from?: string
  date_to?: string
}

export function getAnomalyCasesSummaryV3(p: CasesV3Params = {}): Promise<AnomalyCasesSummary> {
  return http.get<AnomalyCasesSummary>(BASE, {
    ...OPTS,
    params: { view: 'summary', source: 'new', ...p },
  })
}

export function getAnomalyCasesListV3(p: CasesV3Params = {}): Promise<AnomalyCasesListResponse> {
  return http.get<AnomalyCasesListResponse>(BASE, {
    ...OPTS,
    params: { view: 'list', source: 'new', ...p },
  })
}

export function getAnomalyCaseDetailV3(
  caseId: string,
  scenarioId: number,
): Promise<AnomalyCaseDetailResponse> {
  return http.get<AnomalyCaseDetailResponse>(`${BASE}/${encodeURIComponent(caseId)}`, {
    ...OPTS,
    params: { source: 'new', scenario_id: scenarioId },
  })
}

/**
 * Строки, стоящие за одним числом доказательств (сценарии 1–5).
 *
 * Запрос идёт через `view=` на общем пути витрины, а не через подресурс
 * `/{caseId}/evidence/{key}`: приложение на :3001 проксирует сюда сам путь и его
 * известные подресурсы, а произвольный новый сегмент отдаёт 404. Сервис на :8002
 * поддерживает обе формы и отвечает на них одинаково.
 *
 * `limit` всегда `MAX_EVIDENCE_EXAMPLES` — просить больше нельзя, и сервер всё равно
 * обрежет. Ответ приводится к общему контракту панели примеров: `identityStatus` вне
 * `exact` означает, что перечисленные строки не доказаны как набор за числом, и
 * панель обязана сказать это словами, а не показать похожие записи.
 */
export async function getCaseEvidenceExamplesV3(
  caseId: string,
  scenarioId: number,
  evidenceKey: string,
  signal?: AbortSignal,
): Promise<EvidenceExamplesData> {
  const raw = await http.get<EvidenceExamplesData & { identityStatus?: string }>(BASE, {
    ...OPTS,
    signal,
    params: {
      view: 'case-evidence-examples',
      case_id: caseId,
      scenario_id: scenarioId,
      evidence_key: evidenceKey,
      limit: MAX_EVIDENCE_EXAMPLES,
    },
  })
  return { ...raw, status: raw.identityStatus === 'exact' ? 'ok' : 'examples_unavailable' }
}

/**
 * Подробности ОДНОЙ исторической перевозки (сценарии 1–5).
 *
 * Уходит в сеть только по клику пользователя по строке примеров — на отрисовке
 * таблицы запросов не делается ни одного, сколько бы строк в ней ни было.
 *
 * Ключ — точная идентичность обеих ног («файл выгрузки|номер строки»). Ответ со
 * статусом вне `ok` — это отказ, а не пустая карточка: показать «похожую» перевозку
 * вместо запрошенной нельзя.
 *
 * «Не нашли» (404) и «не однозначно» (409) — законные ответы этого ресурса, а не
 * сбои: сервер объясняет их телом со `status`/`reason`, и оно разворачивается
 * обратно в контракт. Настоящая ошибка сети или 5xx остаётся исключением.
 */
export async function getCaseHistoryDetailV3(
  importRecordId: string,
  exportRecordId: string,
  signal?: AbortSignal,
): Promise<HistoryDetailResponse> {
  try {
    return await http.get<HistoryDetailResponse>(BASE, {
      ...OPTS,
      signal,
      params: {
        view: 'case-history-detail',
        import_record_id: importRecordId,
        export_record_id: exportRecordId,
      },
    })
  } catch (e) {
    const body = e instanceof ApiError ? (e.body as HistoryDetailResponse | undefined) : undefined
    if (body && typeof body.status === 'string' && body.status !== 'ok') return body
    throw e
  }
}

/** Похожие аналитические кейсы — подресурс `/{caseId}/similar` (lazy, пагинация). */
export function getSimilarCasesV3(
  caseId: string,
  scenarioId: number,
  page = 1,
  pageSize = 5,
): Promise<SimilarCasesResponse> {
  return http.get<SimilarCasesResponse>(`${BASE}/${encodeURIComponent(caseId)}/similar`, {
    ...OPTS,
    params: { scenario_id: scenarioId, page, pageSize },
  })
}
